<template>
  <v-container fluid>

    <!-- Page Title -->
    <app-page-title
      v-bind:title="'Video Gallery'"
      v-bind:icon="'videocam'"
    />

    <!-- Loading -->
    <template v-if="isLoading">
      <loading
        :active.sync="isLoading"
        :can-cancel="false"
        loader="dots"
        :color="appColor_Progress"
      />
    </template>
    
    <!-- If: No Videos -->
    <template v-if="sourcesBreedsMedia.length === 0 && !isLoading">
      <app-alert v-bind:alertType="'info'" v-bind:message="'No videos found'" />
    </template>

    <!-- Else: Videos Exist -->
    <template v-else>
      <!-- Filter Toolbar -->
      <v-toolbar dense fixed flat dark :color="appColor_Background" :class="!isSmallDevice ? 'mt-n3 mb-2' : 'mt-n1 mb-2'">
        <v-toolbar-items>

          <!-- Animal Source Dropdown -->
          <v-select
            v-model="selectedSourcePath"
            :items="animalSources"
            @change="selectedBreedPath = sourceBreeds[0].path"
            hide-details
            class="mr-4" />
        </v-toolbar-items>

        <v-spacer />

        <!-- Breed Radios -->
        <v-radio-group v-model="selectedBreedPath" row dark hide-details class="mt-3">
          <template v-for="(breed, i) in sourceBreeds">
            <v-radio :key="i" :label="breed.name" :value="breed.path"></v-radio>
          </template>
        </v-radio-group>
      </v-toolbar>

      <!-- Video Thumbnails -->
      <v-card height="110" class="pt-2">
        <v-slide-group
          height="100"
          v-model="selectedVideo"
          mandatory
          show-arrows
          >
          <v-slide-item
            v-for="(item, i) in breedVideos"
            :key="i"
            v-slot:default="{ active, toggle }"
            >
            <span @click="toggle">
              <router-link to="/videoViewer">
                <v-img v-if="!active" :src="getThumbnailUrl(item)" height="90" width="90" class="mx-1" />
                <v-card v-else class="px-1 py-2 elevation-10">
                  <v-img :src="getThumbnailUrl(item)" height="95" width="95" class="mx-1" />
                </v-card>
              </router-link>
            </span>
          </v-slide-item>
        </v-slide-group>
      </v-card>
      <!-- <v-card-text :class="isSmallDevice ? 'mt-4' : 'pt-2'">
        <template v-for="(item, i) in breedVideos">
          <router-link to="/videoViewer" :key="i">
            <span @click="preview(item)">
              <v-img :src="getThumbnailUrl(item)" height="90" width="90"></v-img>
            </span>
          </router-link>
        </template>
      </v-card-text> -->
    </template>

    <v-card class="mt-4 pa-2" height="500">
      <embed width="100%" height="100%" name="plugin" id="plugin" :src="selectedUrl" type="video/mp4">
    </v-card>

    <!-- Error Dialog -->
    <v-dialog v-model="errorDialog" max-width="500px">
      <app-error
        v-bind:errorTitle="errorTitle"
        v-bind:errorText="errorText"
        v-on:close="errorDialog = false"
      />
    </v-dialog>
  </v-container>
</template>

<script>
import appService from '@/app.service'
import AppPageTitle from '@/components/AppPageTitle'
import AppAlert from '@/components/AppAlert'
import AppError from '@/components/AppError'
import { AppColor, MediaPath } from '@/config.js'

export default {
  name: 'VideoViewer',
  components: {
    AppPageTitle,
    AppAlert,
    AppError
  },
  data () {
    return {
      appColor_Background: AppColor.Background,
      appColor_Progress: AppColor.Progress,
      mediaPath_Videos: MediaPath.Videos,

      isLoading: false,
      sourcesBreedsMedia: [],
      animalSources: [],
      selectedSourcePath: null,
      selectedBreedPath: null,
      selectedVideo: null,

      uri: null,

      // preview
      // previewDialog: false,
      // previewFilename: null,
      // previewType: null,
      // previewUrl: null,

      // error handling
      isError: false,
      errorDialog: false,
      errorTitle: 'Internal Error',
      errorText: null
    }
  },
  computed: {
    baseUrl () {
      return `${this.uri}/${this.selectedSourcePath}/${this.selectedBreedPath}`
    },
    sourceBreedsPhotos () {
      if (this.selectedSourcePath == null) return []

      const path = this.selectedSourcePath
      return this.sourcesBreedsMedia.filter(src => { return src.path === path })[0]
    },
    sourceBreeds () {
      if (this.selectedBreedPath == null) return []

      // breeds for the radio buttons
      return this.sourceBreedsPhotos.media
        .map(p => { return {
          path: p.path,
          name: p.breed
        }})
    },
    breedVideos () {
      if (this.selectedBreedPath == null) return []

      const self = this
      const basePath = `${self.uri}/${self.selectedSourcePath}/${self.selectedBreedPath}`

      // imageOptions array for the vue-previewer
      return self.sourceBreedsPhotos.media
        .filter(b => {
            return b.path === self.selectedBreedPath })[0].mediaItems
        .map(v => { return {
          src: `${basePath}/${v.filename}`,
          thumbnailSrc: `${basePath}/thumbnail/${v.filename}`,
          name: v.filename,
          width: '90px',
          height: '90px'
        }})
    },
    isSmallDevice () {
      return this.$vuetify.breakpoint.name === 'xs' || this.$vuetify.breakpoint.name === 'sm'
    },
    selectedUrl () {
      if (this.selectedVideo === null) return null
      const idx = this.selectedVideo
      return `${this.baseUrl}/${this.breedVideos[idx].name}`
    }
  },
  methods: {
    loadData() {
      const self = this
      self.isLoading = true

      appService.getSourcesBreedsVideos().then(data => {
        if (data.errorSeverity > 0) {
          self.isError = true
          self.errorText = data.errorMessage
          self.errorDialog = true
        } else {
          self.uri = data.uri
          self.sourcesBreedsMedia = data.sourcesBreedsMedia

          // construct array for the animal sources dropdown
          self.animalSources = data.sourcesBreedsMedia.map(src => { return {
            value: src.path,
            text: src.source,
          }})

          // default selections
          self.selectedSourcePath = self.animalSources[0].value
          self.selectedBreedPath = self.sourceBreedsPhotos.media[0].path
        }
        self.isLoading = false
      }).catch(() => { self.isLoading = false })        
    },

    getThumbnailUrl (item) {
      const ext = item.name.split('.').pop()
      const url = `${this.baseUrl}/thumbnail/${item.name.replace(ext, 'jpg')}`
      return url
    },

    // preview (item) {
    //   this.previewFilename = item.name
    //   this.previewUrl =  `${this.baseUrl}/${item.name}`
    //   this.previewType = this.mediaPath_Videos
    //   this.previewDialog = true
    // },

    // closePreview () {
    //   this.previewFilename = null
    //   this.previewType = null
    //   this.previewUrl = null
    //   this.previewDialog = false
    // },
  },
  created () {
    this.loadData()
  }
}
</script>